/* eslint-disable no-await-in-loop, no-restricted-syntax */
import { useCallback, useEffect, useRef, useState } from 'react';

import { AgentV } from '@finley-technologies/finley-types';

import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';

/**
 * A single turn in the transcript: the user's prompt plus the ordered content
 * blocks the agent produced in response.
 */
export type AgentVTurn = {
  prompt: string;
  blocks: AgentV.AgentVBlock[];
};

export type AgentVStreamState = {
  turns: AgentVTurn[];
  isStreaming: boolean;
  error: string | null;
  // Bedrock model id reported by the last turnStart frame; null until the first
  // turn streams. Lets the UI show which model is serving the agent.
  model: string | null;
  sendPrompt: (prompt: string) => void;
};

// Replace the block list of the last (in-flight) turn.
const patchLastTurn = (
  turns: AgentVTurn[],
  update: (blocks: AgentV.AgentVBlock[]) => AgentV.AgentVBlock[],
): AgentVTurn[] => {
  if (turns.length === 0) return turns;
  const next = turns.slice();
  const last = next[next.length - 1];
  next[next.length - 1] = { ...last, blocks: update(last.blocks) };
  return next;
};

// Parse one `data: <json>` SSE frame into a validated stream event.
const parseFrame = (rawFrame: string): AgentV.AgentVStreamEvent | null => {
  const dataLine = rawFrame
    .split('\n')
    .find((line) => line.startsWith('data:'));
  if (!dataLine) return null;
  const json = dataLine.slice('data:'.length).trim();
  if (!json) return null;
  try {
    return AgentV.AgentVStreamEventSchema.parse(JSON.parse(json));
  } catch {
    return null;
  }
};

// Open the SSE POST, read the body stream, and dispatch each parsed frame.
// The stream reader inherently needs an await-in-loop; disabled at file scope.
const streamTurn = async ({
  url,
  token,
  request,
  signal,
  onEvent,
}: {
  url: string;
  token: string;
  request: AgentV.AgentVTurnRequest;
  signal: AbortSignal;
  onEvent: (event: AgentV.AgentVStreamEvent) => void;
}): Promise<void> => {
  // Auth is the Bearer header, not a cookie — so no `credentials: 'include'`,
  // which would require Access-Control-Allow-Credentials on the CORS preflight.
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(request),
    signal,
  });

  if (!response.ok || !response.body) {
    throw new Error(`Agent request failed (${response.status}).`);
  }

  const reader = response.body.getReader();
  // Always release the reader lock, even if `read()` throws (abort mid-read or
  // a network error) — otherwise the browser can't reclaim the response body.
  try {
    const decoder = new TextDecoder();
    let buffer = '';
    let done = false;

    while (!done) {
      const chunk = await reader.read();
      done = chunk.done;
      if (chunk.value) {
        buffer += decoder.decode(chunk.value, { stream: true });
      }

      // SSE frames are separated by a blank line.
      let boundary = buffer.indexOf('\n\n');
      while (boundary !== -1) {
        const rawFrame = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        const parsed = parseFrame(rawFrame);
        if (parsed) onEvent(parsed);
        boundary = buffer.indexOf('\n\n');
      }
    }
  } finally {
    reader.cancel().catch(() => {});
  }
};

/**
 * Drives the admin AgentV over the finleycore SSE route
 * (`POST /agent-v/stream`). Native `EventSource` can't send auth headers or a
 * POST body, so we use `fetch` + a `ReadableStream` reader and parse SSE frames
 * by hand.
 *
 * Each `AgentVStreamEvent` is reduced into the current turn's
 * `AgentVBlock[]`: `blockStart` appends the block at its index, `*Delta`
 * patches it, `blockEnd` finalizes it. Step 1 is stateless — every call starts
 * a fresh turn with `threadId: null`.
 *
 * `streamUrl` is dashboard-specific: admin passes
 * `commonUrl.getAdminExpressUrl('agent-v/stream')`, core/backup portals pass
 * `commonUrl.getBorrowerExpressUrl('agent-v/stream')`. Everything else is
 * shared.
 */
export const useAgentVStream = (
  streamUrl: string,
  targetOrganizationId: string,
): AgentVStreamState => {
  const { accessToken } = useUserAuthOrThrow();
  const [turns, setTurns] = useState<AgentVTurn[]>([]);
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [model, setModel] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Abort any in-flight stream on unmount so its reader loop stops and no
  // `setState` fires on an unmounted component.
  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    [],
  );

  // Apply one stream event to the last (in-flight) turn's block list.
  const applyEvent = useCallback((event: AgentV.AgentVStreamEvent): void => {
    switch (event.type) {
      case 'blockStart':
        setTurns((prev) =>
          patchLastTurn(prev, (blocks) => {
            const next = blocks.slice();
            next[event.index] = event.block;
            return next;
          }),
        );
        break;
      case 'textDelta':
        setTurns((prev) =>
          patchLastTurn(prev, (blocks) => {
            const next = blocks.slice();
            const block = next[event.index];
            if (block?.type === 'text') {
              next[event.index] = {
                ...block,
                text: block.text + event.delta,
              };
            }
            return next;
          }),
        );
        break;
      case 'reasoningDelta':
        setTurns((prev) =>
          patchLastTurn(prev, (blocks) => {
            const next = blocks.slice();
            const block = next[event.index];
            if (block?.type === 'reasoning') {
              next[event.index] = {
                ...block,
                summary: block.summary + event.delta,
              };
            }
            return next;
          }),
        );
        break;
      case 'blockEnd':
        setTurns((prev) =>
          patchLastTurn(prev, (blocks) => {
            const next = blocks.slice();
            next[event.index] = event.block;
            return next;
          }),
        );
        break;
      case 'error':
        setError(event.message);
        break;
      case 'turnStart':
        setModel(event.model);
        break;
      // turnEnd / heartbeat need no transcript mutation.
      default:
        break;
    }
  }, []);

  const sendPrompt = useCallback(
    (prompt: string): void => {
      if (isStreaming || !prompt.trim()) return;
      setError(null);
      setIsStreaming(true);
      setTurns((prev) => [...prev, { prompt, blocks: [] }]);

      const controller = new AbortController();
      abortRef.current = controller;

      const request: AgentV.AgentVTurnRequest = {
        threadId: null,
        targetOrganizationId,
        prompt,
      };

      streamTurn({
        url: streamUrl,
        token: accessToken.raw,
        request,
        signal: controller.signal,
        onEvent: applyEvent,
      })
        .catch((err: unknown) => {
          if (!controller.signal.aborted) {
            setError(err instanceof Error ? err.message : 'Stream failed.');
          }
        })
        .finally(() => {
          setIsStreaming(false);
        });
    },
    [accessToken.raw, applyEvent, isStreaming, streamUrl, targetOrganizationId],
  );

  return { turns, isStreaming, error, model, sendPrompt };
};
